import { Button } from '@workspace/ui/components/button';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { m } from '~/paraglide/messages.js';

import type { TeamMemberData } from '../-schemas/org-schema';

type Props = {
  values: TeamMemberData[];
  onNext: () => void;
  onUpdate: (data: TeamMemberData[]) => void;
};

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];

export function AddYourTeamComponent({ onNext, onUpdate }: Props) {
  const [members, setMembers] = useState<TeamMemberData[]>([
    { id: '1', name: '', email: '', role: 'admin' },
  ]);

  const addMember = () => {
    setMembers([
      ...members,
      {
        id: crypto.randomUUID(),
        name: '',
        email: '',
        role: '',
      },
    ]);
  };

  const removeMember = (id: string) => {
    if (members.length > 1) {
      setMembers(members.filter((member) => member.id !== id));
    }
  };

  const updateMember = (
    id: string,
    field: keyof TeamMemberData,
    value: string,
  ) => {
    setMembers(
      members.map((member) =>
        member.id === id ? { ...member, [field]: value } : member,
      ),
    );
  };

  const handleContinue = () => {
    onUpdate(members);
    onNext();
  };

  return (
    <div className="w-full space-y-6 px-4">
      <div className="p-3">
        <p className="text-sm text-gray-300">
          {m['organization.add_team.invitation_type_info']()}
        </p>
      </div>

      <div className="w-full overflow-x-auto rounded-lg border border-gray-700/20 p-3">
        <div className="min-w-[600px]">
          <div className="grid grid-cols-[minmax(120px,1fr)_minmax(180px,1.5fr)_minmax(120px,140px)_40px] gap-3 px-2 mb-3">
            <div className="text-sm font-medium text-gray-400">
              {m['organization.add_team.name_label']()}
            </div>
            <div className="text-sm font-medium text-gray-400">
              {m['organization.add_team.email_label']()}
            </div>
            <div className="text-sm font-medium text-gray-400">
              {m['organization.add_team.role_label']()}
            </div>
            <div className="w-10" />
          </div>

          <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="grid grid-cols-[minmax(120px,1fr)_minmax(180px,1.5fr)_minmax(120px,140px)_40px] gap-3 items-center"
              >
                <input
                  type="text"
                  placeholder={m['organization.add_team.name_placeholder']()}
                  value={member.name}
                  onChange={(e) =>
                    updateMember(member.id, 'name', e.target.value)
                  }
                  className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-[#e4e4e7] focus:outline-none focus:ring-2 focus:ring-[#e4e4e7]/20"
                />

                <input
                  type="email"
                  placeholder={m['organization.add_team.email_placeholder']()}
                  value={member.email}
                  onChange={(e) =>
                    updateMember(member.id, 'email', e.target.value)
                  }
                  className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-[#e4e4e7] focus:outline-none focus:ring-2 focus:ring-[#e4e4e7]/20"
                />

                <select
                  value={member.role}
                  onChange={(e) =>
                    updateMember(member.id, 'role', e.target.value)
                  }
                  className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-4 py-2.5 text-sm text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                >
                  <option value="" disabled>
                    {m['organization.add_team.role_placeholder']()}
                  </option>
                  {ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => removeMember(member.id)}
                  disabled={members.length === 1}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-700 bg-gray-900/50 text-gray-400 transition-colors hover:border-red-500 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-700 disabled:hover:text-gray-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4">
        <button
          type="button"
          onClick={addMember}
          className="flex items-center gap-2 text-sm font-medium text-[#e4e4e7] transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-[#e4e4e7]/20 rounded px-2 py-1"
        >
          {m['organization.add_team.add_member']()}
          <Plus className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400">
            {members.filter((m) => m.name && m.email && m.role).length} members
          </span>

          <Button
            onClick={handleContinue}
            className="flex items-center justify-center gap-2 rounded-lg px-6 py-6 font-semibold transition-all"
          >
            {m['common.continue']()}
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
